/**
 * @fileoverview Adapter that bridges Recharts Tooltip props to AnchoredHoverCard anchored at a virtual point.
 * @module shared/components/ui/RechartsHoverAdapter
 */

import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { AnchoredHoverCard } from './AnchoredHoverCard'

export interface RechartsHoverAdapterProps {
  /** data-chart attribute id from ChartContainer (e.g., "chart-cost-vs-profit-donut") */
  dataChartId: string
  /** Fixed width for the hovercard (default 420) */
  width?: number
  /** Estimated height for initial placement (default 320) */
  estimatedHeight?: number
  /** Margin from viewport edges (default 8) */
  margin?: number
  /** Anchor offset relative to the cursor (default { x: 16, y: -16 }) */
  offset?: { x?: number; y?: number }
  /** When true, pointer events disabled (default true) */
  inert?: boolean
  /** Follow mouse cursor instead of anchoring to data point (default true) */
  followMouse?: boolean
  /** Disable tooltip rendering entirely (useful for mobile where Sheet is used) */
  disabled?: boolean
  /** When true, position changes are animated with spring physics (default true) */
  animatePosition?: boolean
  /** Render content using Recharts tooltip context */
  children: (ctx: { label?: unknown; payload?: unknown[] }) => ReactNode
}

/**
 * This component is intended to be passed as the `content` prop to Recharts Tooltip.
 * Keep ChartTooltip wrapper hidden via wrapperStyle={{ display: 'none' }}.
 */
export function RechartsHoverAdapter(
  props: RechartsHoverAdapterProps & {
    active?: boolean
    payload?: unknown[]
    label?: unknown
    // Recharts adds a non-typed coordinate prop
    [key: string]: unknown
  }
): ReactElement | null {
  const {
    dataChartId,
    width = 420,
    estimatedHeight = 320,
    margin = 8,
    offset = { x: 16, y: -16 },
    inert = true,
    followMouse = true,
    disabled = false,
    animatePosition = true,
  } = props

  const activeFromRecharts = Boolean(props.active)
  const payload = useMemo(() => (props.payload as unknown[]) || [], [props.payload])
  const label = props.label

  const coordinate =
    (props as unknown as { coordinate?: { x?: number; y?: number } }).coordinate || {}
  const cx = coordinate.x ?? 0
  const cy = coordinate.y ?? 0

  const [dismissed, setDismissed] = useState(false)
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)

  // Async debounced active state - delays hide until next animation frame
  // This prevents tooltip from flickering when moving between donut slices
  const [debouncedActive, setDebouncedActive] = useState(false)
  const hideFrameRef = useRef<number | null>(null)

  useEffect(() => {
    if (activeFromRecharts) {
      // Cancel any pending hide
      if (hideFrameRef.current !== null) {
        cancelAnimationFrame(hideFrameRef.current)
        hideFrameRef.current = null
      }
      // Show immediately via microtask to satisfy lint rule
      queueMicrotask(() => setDebouncedActive(true))
    } else {
      // Delay hide until next frame to allow moving between slices
      hideFrameRef.current = requestAnimationFrame(() => {
        setDebouncedActive(false)
        hideFrameRef.current = null
      })
    }

    return () => {
      if (hideFrameRef.current !== null) {
        cancelAnimationFrame(hideFrameRef.current)
      }
    }
  }, [activeFromRecharts])

  // Track pointer movement to (a) follow cursor if enabled and (b) re-open after dismissal.
  // Uses pointermove (not mousemove) to support touch devices like tablets.
  useEffect(() => {
    // Skip effect when disabled
    if (disabled || !debouncedActive) return

    // Get container reference
    containerRef.current = document.querySelector(`[data-chart="${dataChartId}"]`) as HTMLElement | null

    const handlePointerMove = (e: PointerEvent): void => {
      if (dismissed) {
        setDismissed(false)
      }
      if (followMouse) {
        setMousePos({ x: e.clientX, y: e.clientY })
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('pointermove', handlePointerMove, { passive: true })
    }

    return () => {
      if (container) {
        container.removeEventListener('pointermove', handlePointerMove)
      }
    }
  }, [debouncedActive, dataChartId, dismissed, followMouse, disabled])

  // Compute fallback anchor from chart coordinate
  const chartAnchor = (() => {
    const container = document.querySelector(`[data-chart="${dataChartId}"]`) as HTMLElement | null
    const rect = container?.getBoundingClientRect()
    return {
      x: (rect?.left ?? 0) + cx,
      y: (rect?.top ?? 0) + cy,
    }
  })()

  // Use mouse position if tracking, otherwise fall back to chart coordinate
  const anchor = followMouse && mousePos ? mousePos : chartAnchor

  // Dismiss tooltip on scroll or global pointer down so it never "sticks" to
  // the viewport when the user scrolls the dashboard or clicks elsewhere.
  useEffect(() => {
    // Skip effect when disabled
    if (disabled) return

    const effectiveActive = debouncedActive && !dismissed
    if (!effectiveActive) return

    const handleScroll = (): void => {
      setDismissed(true)
    }

    const handlePointerDown = (): void => {
      setDismissed(true)
    }

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('wheel', handleScroll, true)
    window.addEventListener('pointerdown', handlePointerDown, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('wheel', handleScroll, true)
      window.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [debouncedActive, dismissed, disabled])

  // Return null when disabled or not active
  const effectiveActive = debouncedActive && !dismissed && !disabled

  if (!effectiveActive) return null

  return (
    <AnchoredHoverCard
      active={effectiveActive}
      anchor={anchor}
      width={width}
      estimatedHeight={estimatedHeight}
      margin={margin}
      offset={offset}
      inert={inert}
      animatePosition={animatePosition}
    >
      {props.children({ label, payload })}
    </AnchoredHoverCard>
  )
}
